import { useState } from 'react';
import fmImage from '../../fm.png';
import { Calendar, Users, Trophy, Sparkles, MapPin, Clock, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import dadoLogo from 'figma:asset/436af43621e018c50b65365e8b2e98a1fa445235.png';

interface ActivityItem {
  id: number;
  title: string;
  type: '比赛' | '工作坊' | '挑战' | '聚会';
  status: '进行中' | '即将开始' | '报名中' | '长期有效';
  image: string;
  date: string;
  location?: string;
  participants?: number;
  description: string;
  details: string;
  tags: string[];
}

const activities: ActivityItem[] = [
  {
    id: 1,
    title: '2026通过领红包挑战',
    type: '比赛',
    status: '进行中',
    image: fmImage,
    date: '2026.2/1 - 2026.2/25日',
    description: '参与活动通关，可免费领取红包封面！',
    details: '',
    tags: [],
  },
];

export function Activity() {
  const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);
  const navigate = useNavigate();

  const getStatusColor = (status: string) => {
    switch (status) {
      case '进行中':
        return 'bg-red-500';
      case '即将开始':
        return 'bg-blue-500';
      case '报名中':
        return 'bg-orange-500';
      case '长期有效':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case '比赛':
        return <Trophy className="w-5 h-5" />;
      case '工作坊':
        return <Users className="w-5 h-5" />;
      case '挑战':
        return <Sparkles className="w-5 h-5" />;
      case '聚会':
        return <Calendar className="w-5 h-5" />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-white pt-[57px]">
      {/* 活动展示 */}
      <div className="max-w-[1680px] mx-auto px-12 py-16">
        <div className="max-w-2xl mx-auto">
          {activities.map((activity) => (
            <div 
              key={activity.id}
              onClick={() => window.open('/game2/index.html', '_blank')}
              className="group cursor-pointer bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              {/* 图片 */}
              <div className="relative h-[340px] md:h-[360px] overflow-hidden">
                <img
                  src={activity.image}
                  alt={activity.title}
                  className="w-full h-full object-cover object-center transition-transform duration-300"
                />
                {/* 状态标签 */}
                <div className={`absolute top-4 left-4 ${getStatusColor(activity.status)} text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg`}>
                  {activity.status}
                </div>
                {/* 类型图标 */}
                <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm p-2.5 rounded-full shadow-lg">
                  {getTypeIcon(activity.type)}
                </div>
              </div>

              {/* 内容 */}
              <div className="p-8">
                <h3 className="text-3xl font-bold mb-4 group-hover:text-red-600 transition-colors" style={{ fontFamily: '"Noto Sans SC", sans-serif' }}>
                  {activity.title}
                </h3>
                <p className="text-gray-600 mb-6 text-lg" style={{ fontFamily: '"Noto Sans SC", sans-serif' }}>
                  {activity.description}
                </p>

                {/* 信息栏 */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-base text-gray-500">
                    <Calendar className="w-5 h-5" />
                    <span>{activity.date}</span>
                  </div>
                  {activity.location && (
                    <div className="flex items-center gap-3 text-base text-gray-500">
                      <MapPin className="w-5 h-5" />
                      <span>{activity.location}</span>
                    </div>
                  )}
                  {/* 参与人数移除 */}
                </div>

                {/* 标签 */}
                {/* 标签移除 */}

                {/* dado logo - 跳转到工具页 */}
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/');
                    }}
                    className="flex flex-col items-center gap-2 mx-auto hover:opacity-60 transition-opacity"
                  >
                    <img 
                      src={dadoLogo} 
                      alt="dado" 
                      className="h-4 object-contain"
                    />
                    <span className="text-xs text-gray-400">原创开发小工具</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 活动详情模态框 */}
      {selectedActivity && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-8"
          onClick={() => setSelectedActivity(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 头部图片移除，仅保留关闭与状态 */}
            <div className="relative h-20">
              <button
                onClick={() => setSelectedActivity(null)}
                className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm p-2 rounded-full hover:bg-white transition-all shadow-lg"
              >
                <X className="w-6 h-6" />
              </button>
              <div className={`absolute top-6 left-6 ${getStatusColor(selectedActivity.status)} text-white text-sm font-medium px-4 py-2 rounded-full shadow-lg`}>
                {selectedActivity.status}
              </div>
            </div>

            {/* 内容 */}
            <div className="p-8 overflow-y-auto max-h-[calc(90vh-320px)]">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-gray-100 rounded-full">
                  {getTypeIcon(selectedActivity.type)}
                </div>
                <div>
                  <p className="text-sm text-gray-500">{selectedActivity.type}</p>
                  <h2 className="text-3xl font-bold" style={{ fontFamily: '"Noto Sans SC", sans-serif' }}>
                    {selectedActivity.title}
                  </h2>
                </div>
              </div>

              {/* 信息栏 */}
              <div className="grid grid-cols-2 gap-4 mb-8 p-6 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-xs text-gray-500">时间</p>
                    <p className="font-medium">{selectedActivity.date}</p>
                  </div>
                </div>
                {selectedActivity.location && (
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">地点</p>
                      <p className="font-medium">{selectedActivity.location}</p>
                    </div>
                  </div>
                )}
                {selectedActivity.participants && (
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">参与人数</p>
                      <p className="font-medium">{selectedActivity.participants} 人</p>
                    </div>
                  </div>
                )}
              </div>

              {/* 详细介绍移除，后续由你补充内容 */}

              {/* 标签移除 */}

              {/* 底部按钮移除 */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
